import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

function isHTML(str) {
  return /<[a-z][\s\S]*>/i.test(str);
}

function stripTags(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function getCharLimit(el) {
  if (!el) return 400;
  const style = window.getComputedStyle(el);
  const fontSize = parseFloat(style.fontSize) || 16;
  const width = el.clientWidth || 300;

  const test = document.createElement("span");
  test.style.cssText = `
    position: absolute; visibility: hidden; white-space: nowrap;
    font-size: ${fontSize}px; font-family: ${style.fontFamily};
  `;
  test.textContent = "abcdefghijklmnopqrstuvwxyz ABCDEFGHIJ0123456789";
  document.body.appendChild(test);
  const avgCharWidth = test.offsetWidth / test.textContent.length;
  document.body.removeChild(test);

  const charsPerLine = Math.floor(width / avgCharWidth);
  return Math.max(100, charsPerLine * 4);
}

function truncateHTMLToLimit(html, limit) {
  const div = document.createElement("div");
  div.innerHTML = html;
  let count = 0;
  let done = false;

  function walk(node) {
    if (done) return;
    if (node.nodeType === 3) {
      const remaining = limit - count;
      if (node.textContent.length > remaining) {
        node.textContent = node.textContent.slice(0, remaining);
        done = true;
      } else {
        count += node.textContent.length;
      }
    } else {
      for (const child of [...node.childNodes]) {
        walk(child);
        if (done) break;
      }
      if (done) {
        let next = node.nextSibling;
        while (next) {
          const toRemove = next;
          next = next.nextSibling;
          toRemove.parentNode && toRemove.parentNode.removeChild(toRemove);
        }
      }
    }
  }

  walk(div);
  return div;
}

function injectToggleIntoDOM(container, toggleEl) {
  function getLastTextParent(node) {
    const children = [...node.childNodes].reverse();
    for (const child of children) {
      if (child.nodeType === 3 && child.textContent.trim()) {
        return node;
      }
      if (child.nodeType === 1) {
        const found = getLastTextParent(child);
        if (found) return found;
      }
    }
    return null;
  }

  const parent = getLastTextParent(container);
  if (parent) {
    parent.appendChild(toggleEl);
  } else {
    container.appendChild(toggleEl);
  }
}

const SeeMoreLess = ({ text = "", maxChars = null }) => {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);
  const [charLimit, setCharLimit] = useState(200);
  const [ready, setReady] = useState(false);

  const html = isHTML(text);
  const rawText = html ? stripTags(text) : text;

  useLayoutEffect(() => {
    if (containerRef.current) {
      const limit = maxChars ?? getCharLimit(containerRef.current);
      setCharLimit(limit);
      setReady(true);
    }
  }, [text, maxChars]);

  const needsToggle = rawText.length > charLimit;

  useEffect(() => {
    if (!ready || !html || !containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = "";

    if (!needsToggle || expanded) {
      container.innerHTML = text;
      if (needsToggle && expanded) {
        const toggle = document.createElement("span");
        toggle.className = "rich-see-toggle";
        toggle.textContent = " Less";
        toggle.onclick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          setExpanded(false);
        };
        injectToggleIntoDOM(container, toggle);
      }
      return;
    }

    const truncatedDiv = truncateHTMLToLimit(text, charLimit);
    while (truncatedDiv.firstChild) {
      container.appendChild(truncatedDiv.firstChild);
    }

    const toggle = document.createElement("span");
    toggle.className = "rich-see-toggle";
    toggle.textContent = "...More";
    toggle.onclick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setExpanded(true);
    };
    injectToggleIntoDOM(container, toggle);
  }, [ready, expanded, text, charLimit, html, needsToggle]);

  return (
    <div>
      <style>{`
        .rich-see-more-content {
          line-height: 1.6;
          color: inherit;
          word-break: break-word;
        }
        .rich-see-more-content p {
          margin: 6px 0 !important;
        }
        .rich-see-more-content ul,
        .rich-see-more-content ol {
          list-style: none !important;
          padding-left: 0 !important;
          margin: 8px 0 !important;
        }
        .rich-see-more-content ul li,
        .rich-see-more-content ol li {
          display: block !important;
          position: relative !important;
          padding-left: 16px !important;
          margin: 6px 0 !important;
        }
        .rich-see-more-content ul li::before {
          content: "•" !important;
          position: absolute !important;
          left: 4px !important;
          top: 0 !important;
          color: inherit !important;
          font-weight: bold !important;
        }
        .rich-see-more-content ol {
          counter-reset: item;
        }
        .rich-see-more-content ol li {
          counter-increment: item;
        }
        .rich-see-more-content ol li::before {
          content: counter(item) "." !important;
          position: absolute !important;
          left: 4px !important;
          top: 0 !important;
          color: inherit !important;
          font-weight: bold !important;
        }
        .rich-see-more-content li p {
          display: inline !important;
          margin: 0 !important;
        }
        .rich-see-more-content strong,
        .rich-see-more-content b {
          font-weight: 700 !important;
        }
        .rich-see-more-content h1,
        .rich-see-more-content h2,
        .rich-see-more-content h3,
        .rich-see-more-content h4,
        .rich-see-more-content h5,
        .rich-see-more-content h6 {
          margin: 10px 0 !important;
          font-weight: 700 !important;
        }
        .rich-see-more-content,
        .rich-see-more-content * {
          background: transparent !important;
          background-color: transparent !important;
          background-image: none !important;
        }
        .rich-see-toggle {
  all: unset !important;
  color: #2092c7 !important;
  cursor: pointer !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  font-family: inherit !important;
  line-height: 1.4 !important;
  display: inline !important;
  margin-left: 4px !important;
  position: relative !important;
  z-index: 10 !important;
  white-space: nowrap !important;
}
      `}</style>

      {html && <div ref={containerRef} className="rich-see-more-content" />}

      {!html && (
        <div ref={containerRef} className="rich-see-more-content">
          <span>
            {needsToggle && !expanded
              ? rawText.slice(0, charLimit) + "..."
              : rawText}
          </span>
          {needsToggle && (
            <span
              className="rich-see-toggle"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setExpanded(!expanded);
              }}
            >
              {expanded ? " Less" : "..More"}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SeeMoreLess;
